import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BriefcaseBusiness, Building2, CalendarDays, CheckCircle2, ClipboardCheck,
  History, Loader2, MessageSquarePlus, Plus, Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

export const EMPLOYMENT_STATUS_TONES = {
  Joined: "bg-blue-50 text-blue-700 border-blue-200",
  "Invoice Pending": "bg-amber-50 text-amber-800 border-amber-300",
  "Payment Received": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Left: "bg-red-50 text-red-700 border-red-200",
  // legacy statuses (retired from the active flow, kept for old records)
  Working: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Lock Complete": "bg-violet-50 text-violet-700 border-violet-200",
  "Ready for Invoice": "bg-amber-50 text-amber-800 border-amber-300",
};

export const EmploymentStatusBadge = ({ status, className = "" }) => {
  if (!status) return null;
  return (
    <Badge variant="outline" data-testid="employment-status-badge"
      className={`rounded-full font-medium whitespace-nowrap ${EMPLOYMENT_STATUS_TONES[status] || "bg-muted text-foreground"} ${className}`}>
      {status}
    </Badge>
  );
};

const CreateEmploymentDialog = ({ open, onOpenChange, onCreated }) => {
  const navigate = useNavigate();
  const [selectedApps, setSelectedApps] = useState([]);
  const [appId, setAppId] = useState("");
  const [prep, setPrep] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [template, setTemplate] = useState("");
  const [joining, setJoining] = useState("");
  const [dateSelected, setDateSelected] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/employment/selected-applications").then((res) => setSelectedApps(res.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!appId) { setPrep(null); return; }
    api.get("/employment/prepare", { params: { application_id: appId } })
      .then((res) => {
        setPrep(res.data);
        // Auto-select company only when there is EXACTLY one match
        setCompanyId(res.data.company_matches?.length === 1 ? res.data.company_matches[0].id : "");
        setDateSelected((res.data.suggested_date_selected || "").slice(0, 10));
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [appId]);

  const reset = () => {
    setAppId(""); setPrep(null); setCompanyId(""); setTemplate(""); setJoining(""); setDateSelected(""); setRemarks("");
  };

  const tpl = prep?.locking_templates?.find((t) => t.label === template);
  const lockEndPreview = (() => {
    if (!joining || !tpl?.value) return null;
    const d = new Date(joining + "T00:00:00Z");
    if (tpl.unit === "Months") d.setUTCMonth(d.getUTCMonth() + tpl.value);
    else d.setUTCDate(d.getUTCDate() + tpl.value);
    return d.toISOString().slice(0, 10);
  })();

  const submit = async () => {
    if (!appId) return toast.error("Choose a Selected application");
    if (!companyId) return toast.error("Choose the company from Company Master");
    if (!joining) return toast.error("Enter the date of joining");
    if (!template) return toast.error("Choose a locking period template");
    setBusy(true);
    try {
      const payload = { application_id: appId, company_id: companyId, date_of_joining: joining, locking_template: template };
      if (dateSelected) payload.date_selected = dateSelected;
      if (remarks.trim()) payload.remarks = remarks.trim();
      const res = await api.post("/employment", payload);
      toast.success(`Employment ${res.data.emp_code} created`);
      onOpenChange(false);
      reset();
      onCreated?.();
      navigate(`/admin/employment/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing_id) {
        toast.info(detail.message);
        onOpenChange(false);
        reset();
        navigate(`/admin/employment/${detail.existing_id}`);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" data-testid="create-employment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New employment record</DialogTitle>
          <DialogDescription>
            Created from a Selected application. Locking period is copied permanently from the chosen template.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Selected application <span className="text-destructive">*</span></Label>
            <Select value={appId} onValueChange={setAppId}>
              <SelectTrigger data-testid="employment-application-select"><SelectValue placeholder="Choose a Selected application..." /></SelectTrigger>
              <SelectContent>
                {selectedApps.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.app_code} — {a.candidate?.full_name} · {a.job?.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedApps.length === 0 && <p className="text-xs text-muted-foreground">No Selected applications without an employment record.</p>}
          </div>

          {prep && (
            <>
              {prep.active_employment && (
                <p className="text-xs rounded-lg bg-red-50 text-red-700 border border-red-200 px-3 py-2" data-testid="active-employment-warning">
                  This candidate already has an active employment ({prep.active_employment.emp_code}, {prep.active_employment.status}).
                </p>
              )}
              <div className="space-y-1.5">
                <Label>Company (from Company Master) <span className="text-destructive">*</span></Label>
                {prep.company_matches?.length === 1 && companyId && (
                  <p className="text-xs text-emerald-700" data-testid="company-automatch-note">
                    Auto-matched with “{prep.company_matches[0].name}” from the job’s company label.
                  </p>
                )}
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger data-testid="employment-company-select"><SelectValue placeholder={`No exact match for "${prep.job?.hiring_brand || ""}" — choose manually...`} /></SelectTrigger>
                  <SelectContent>
                    {prep.companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                  </SelectContent>
                </Select>
                {prep.companies.length === 0 && <p className="text-xs text-destructive">No companies in Company Master yet — create the company first.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Date selected</Label>
                  <Input type="date" data-testid="employment-date-selected-input" value={dateSelected} onChange={(e) => setDateSelected(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Date of joining <span className="text-destructive">*</span></Label>
                  <Input type="date" data-testid="employment-joining-date-input" value={joining} onChange={(e) => setJoining(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Locking period template <span className="text-destructive">*</span></Label>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger data-testid="employment-locking-select"><SelectValue placeholder="Choose from Master Data..." /></SelectTrigger>
                  <SelectContent>
                    {prep.locking_templates.map((t) => <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {lockEndPreview && (
                  <p className="text-xs text-muted-foreground" data-testid="locking-end-preview">
                    Locking end date: <span className="font-medium text-foreground">{formatDate(lockEndPreview)}</span>
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Remarks</Label>
                <Textarea data-testid="employment-remarks-input" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional context..." />
              </div>
            </>
          )}

          <Button onClick={submit} disabled={busy} className="w-full rounded-lg" data-testid="create-employment-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create employment record
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------- recruiter
// Phase 33: task-driven Joining Confirmation. Milestone (20/40/60/90 day)
// tasks appear ONLY when due; submitting a remark completes the task and it
// leaves the active list until the next milestone becomes due.

const todayIso = () => new Date().toISOString().slice(0, 10);

const DuePill = ({ due }) => {
  if (due < todayIso())
    return <Badge variant="outline" className="rounded-full bg-red-50 text-red-700 border-red-200" data-testid="jc-due-badge">Overdue</Badge>;
  return <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200" data-testid="jc-due-badge">Due today</Badge>;
};

const RecruiterJoiningConfirmation = () => {
  const [tab, setTab] = useState("active");
  const [active, setActive] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [remarkOf, setRemarkOf] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(false);
    Promise.all([
      api.get("/followups", { params: { bucket: "actionable" } }),
      api.get("/followups", { params: { bucket: "resolved" } }),
      api.get("/employment", { params: { status: "all" } }),
    ])
      .then(([a, r, e]) => {
        setActive((a.data || []).filter((f) => f.employment_id));
        setResolved((r.data || []).filter((f) => f.employment_id)
          .sort((x, y) => (y.completed_date || "").localeCompare(x.completed_date || "")));
        setRecords(e.data || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRemark = async () => {
    if (remark.trim().length < 2) return toast.error("Write the joining confirmation remark first");
    setBusy(true);
    try {
      await api.put(`/followups/${remarkOf.id}/remark`, { text: remark.trim() });
      toast.success(`${remarkOf.followup_type} confirmed — this candidate will return when the next milestone is due`);
      setRemarkOf(null);
      setRemark("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-10 w-72 rounded-lg" />{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Joining Confirmation</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Confirm each 20/40/60/90-day milestone with a remark. Confirmed candidates come back automatically when the next milestone is due.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg flex-wrap h-auto">
          <TabsTrigger value="active" className="rounded-md" data-testid="jc-tab-active">
            Action needed{active.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold h-4 min-w-4 px-1">{active.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-md" data-testid="jc-tab-history">Completed</TabsTrigger>
          <TabsTrigger value="records" className="rounded-md" data-testid="jc-tab-records">All records</TabsTrigger>
        </TabsList>
      </Tabs>

      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="jc-error">
          <p className="text-sm text-muted-foreground">Could not load joining confirmations.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : tab === "active" ? (
        active.length === 0 ? (
          <EmptyState icon={ClipboardCheck} title="Nothing to confirm right now"
            description="Milestone follow-ups appear here only when they are due. Confirmed candidates return at the next milestone." />
        ) : (
          <Card className="rounded-xl border-border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Milestone</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Employment</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.map((f) => (
                    <TableRow key={f.id} className="hover:bg-muted/60" data-testid="jc-active-row">
                      <TableCell>
                        <p className="font-medium text-sm">{f.candidate?.full_name || "—"}</p>
                        <p className="text-xs text-muted-foreground font-mono">{f.candidate?.code}</p>
                      </TableCell>
                      <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{f.company_label || "—"}</span></TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full bg-sky-50 text-sky-700 border-sky-200">{f.followup_type}</Badge></TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{formatDate(f.due_date)}</span>
                          <DuePill due={f.due_date} />
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {f.employment ? (
                          <Link to={`/recruiter/employment/${f.employment.id}`} className="text-primary hover:underline" data-testid={`jc-emp-link-${f.id}`}>{f.employment.emp_code}</Link>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" className="h-8 rounded-lg"
                          onClick={() => { setRemarkOf(f); setRemark(""); }} data-testid={`jc-confirm-${f.id}`}>
                          <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> Confirm & add remark
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      ) : tab === "history" ? (
        resolved.length === 0 ? (
          <EmptyState icon={History} title="No completed confirmations yet"
            description="Milestones you confirm will appear here with your remark and timestamp." />
        ) : (
          <Card className="rounded-xl border-border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Milestone</TableHead>
                    <TableHead>Remark</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Resolved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolved.map((f) => (
                    <TableRow key={f.id} className="hover:bg-muted/60" data-testid="jc-history-row">
                      <TableCell>
                        <p className="font-medium text-sm">{f.candidate?.full_name || "—"}</p>
                        <p className="text-xs text-muted-foreground font-mono">{f.candidate?.code}</p>
                      </TableCell>
                      <TableCell className="text-sm">{f.followup_type}</TableCell>
                      <TableCell className="text-sm max-w-[280px]">
                        {f.recruiter_remark || f.notes || f.skip_reason ? (
                          <div data-testid={`jc-history-remark-${f.id}`}>
                            <p className="whitespace-pre-line line-clamp-2">{f.recruiter_remark || f.notes || f.skip_reason}</p>
                            {f.remark_by && <p className="text-[11px] text-muted-foreground mt-0.5">{f.remark_by} · {formatDateTime(f.remark_at)}</p>}
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {f.status === "Completed"
                          ? <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>
                          : <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-600 border-slate-200">Skipped</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(f.completed_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      ) : (
        records.length === 0 ? (
          <EmptyState icon={BriefcaseBusiness} title="No employment records"
            description="Employment records will appear here once created by an admin." />
        ) : (
          <Card className="rounded-xl border-border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Joining</TableHead>
                    <TableHead>Lock ends</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((e) => (
                    <TableRow key={e.id} className="hover:bg-muted/60" data-testid="jc-record-row">
                      <TableCell className="font-mono text-xs">
                        <Link to={`/recruiter/employment/${e.id}`} className="text-primary hover:underline" data-testid={`employment-link-${e.id}`}>{e.emp_code}</Link>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{e.candidate?.full_name || "—"}</p>
                        <p className="text-xs text-muted-foreground font-mono">{e.candidate?.code}</p>
                      </TableCell>
                      <TableCell className="text-sm"><span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{e.company?.name || "—"}</span></TableCell>
                      <TableCell className="text-sm"><span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{formatDate(e.date_of_joining)}</span></TableCell>
                      <TableCell className="text-sm">{formatDate(e.locking_end_date)}</TableCell>
                      <TableCell><EmploymentStatusBadge status={e.status} className="text-[11px]" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      )}

      {/* Milestone confirmation remark dialog */}
      <Dialog open={!!remarkOf} onOpenChange={(v) => !v && setRemarkOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="jc-remark-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {remarkOf?.followup_type} — {remarkOf?.candidate?.full_name}
            </DialogTitle>
            <DialogDescription>
              Your remark is timestamped and attached to this specific milestone. Submitting completes it — the candidate returns automatically when the next milestone is due.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Joining confirmation remark <span className="text-destructive">*</span></Label>
              <Textarea rows={3} className="bg-white" value={remark} placeholder="e.g. Spoke to the candidate — working fine, salary credited on time..."
                onChange={(e) => setRemark(e.target.value)} data-testid="jc-remark-input" />
            </div>
            <Button onClick={submitRemark} disabled={busy} className="w-full rounded-lg" data-testid="jc-remark-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit & complete milestone
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminEmploymentRegister = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const basePath = isAdmin ? "/admin/employment" : "/recruiter/employment";
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState("");
  const [companyId, setCompanyId] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/employment", { params: {
      status, q: q || undefined,
      month: month || undefined,
      company_id: companyId !== "all" ? companyId : undefined,
    } })
      .then((res) => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, q, month, companyId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/master-data").then((res) => setStatuses(res.data.employment_record_status || [])).catch(() => {});
    api.get("/companies").then((res) => {
      const items = Array.isArray(res.data) ? res.data : res.data.items || [];
      setCompanies(items.map((c) => ({ id: c.id, name: c.name })));
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">{isAdmin ? "Employment Register" : "Joining Confirmation"}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isAdmin ? "Post-selection lifecycle — joining, locking period, and invoice readiness." : "Read-only view. You can add follow-up remarks."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)} className="rounded-lg shrink-0" data-testid="new-employment-button">
            <Plus className="h-4 w-4 mr-2" /> New Employment
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="employment-search" placeholder="Search employment ID, candidate, company, job..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="w-full sm:w-44 bg-white" aria-label="Month of joining" data-testid="employment-month-filter" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="employment-company-filter">
            <SelectValue placeholder="Filter by company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All companies</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="employment-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={BriefcaseBusiness} title="No employment records"
          description={isAdmin ? "Create the first record from a Selected application." : "Employment records will appear here once created by an admin."} />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Joining</TableHead>
                  <TableHead>Lock ends</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="hover:bg-muted/60 cursor-pointer" data-testid="employment-row">
                    <TableCell className="font-mono text-xs">
                      <Link to={`${basePath}/${e.id}`} className="text-primary hover:underline" data-testid={`employment-link-${e.id}`}>{e.emp_code}</Link>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{e.candidate?.full_name || "—"}</p>
                      <p className="text-xs text-muted-foreground font-mono">{e.candidate?.code}</p>
                    </TableCell>
                    <TableCell className="text-sm"><span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{e.company?.name || "—"}</span></TableCell>
                    <TableCell className="text-sm">{e.job?.title || "—"}</TableCell>
                    <TableCell className="text-sm"><span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{formatDate(e.date_of_joining)}</span></TableCell>
                    <TableCell className="text-sm">{formatDate(e.locking_end_date)}</TableCell>
                    <TableCell><EmploymentStatusBadge status={e.status} className="text-[11px]" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {isAdmin && <CreateEmploymentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />}
    </div>
  );
};

export default function EmploymentList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  // Phase 33: recruiters get the task-driven Joining Confirmation workflow;
  // the admin Employment Register stays exactly as before.
  if (!isAdmin) return <RecruiterJoiningConfirmation />;
  return <AdminEmploymentRegister />;
}
